// src/components/ui/InstaxImage.tsx

"use client"

import Image, { type StaticImageData, type ImageProps } from "next/image"
import { memo } from "react"
import { cx } from "~/lib/utils"

interface InstaxImageProps extends Omit<ImageProps, 'alt'> {
  className?: string
  alt: string
  caption?: string
  src: string | StaticImageData
  width: number
  height: number
  priority?: boolean
  loading?: "lazy" | "eager"
  quality?: number
  placeholder?: "blur" | "empty"
  blurDataURL?: string
  sizes?: string
}

const blurDataURL = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg=="

export const InstaxImage = memo(function InstaxImage({
  className,
  src,
  width,
  height,
  alt,
  caption,
  priority,
  loading,
  quality,
  placeholder,
  blurDataURL: customBlurDataURL,
  sizes,
  ...rest
}: InstaxImageProps) {
  return (
    <figure
      className={cx(
        "group relative h-fit overflow-hidden transition-all duration-300",
        "hover:shadow-lg hover:shadow-black/10 dark:hover:shadow-indigo-500/10",
        className
      )}>
      <div className="relative overflow-hidden">
        <Image 
          src={src} 
          alt={alt} 
          width={width} 
          height={height} 
          className="transition-transform duration-300 group-hover:scale-102"
          priority={priority}
          loading={loading}
          quality={quality}
          placeholder={placeholder ?? "blur"}
          blurDataURL={customBlurDataURL ?? blurDataURL}
          sizes={sizes}
          {...rest}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
      </div>
      {caption && (
        <figcaption className="absolute bottom-0 left-0 right-0 bg-black/40 p-2 text-center font-sans text-sm text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          {caption}
        </figcaption>
      )}
    </figure>
  )
})

interface InstaxImageNoCapProps extends Omit<ImageProps, 'alt'> {
  className?: string
  alt: string
  src: string | StaticImageData
  width: number
  height: number
  priority?: boolean
  loading?: "lazy" | "eager"
  quality?: number
  placeholder?: "blur" | "empty"
  blurDataURL?: string
  sizes?: string
}

export const InstaxImageNoCap = memo(function InstaxImageNoCap({
  className,
  src,
  width,
  height,
  alt,
  priority,
  loading,
  quality,
  placeholder,
  blurDataURL: customBlurDataURL,
  sizes,
  ...rest
}: InstaxImageNoCapProps) {
  return (
    <figure
      className={cx(
        "h-fit overflow-hidden rounded-lg bg-white shadow-xl shadow-black/10 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-black/20 dark:bg-gray-900 dark:shadow-indigo-500/5 dark:ring-white/20 dark:hover:shadow-indigo-900/20",
        className
      )}
      style={{ width: `${width}px` }}
    >
      <div className="bg-gray-50 p-2 dark:bg-gray-900">
        <div className="relative overflow-hidden rounded">
          <div className="absolute inset-0 shadow-[inset_0px_0px_3px_0px_rgb(0,0,0,1)]"></div>
          <Image 
            src={src} 
            alt={alt} 
            width={width} 
            height={height} 
            priority={priority}
            loading={loading}
            quality={quality}
            placeholder={placeholder ?? "blur"}
            blurDataURL={customBlurDataURL ?? blurDataURL}
            sizes={sizes}
            {...rest}
          />
        </div>
      </div>
    </figure>
  )
})
